import type {
  NavItem,
  NavTranslate,
  PublicNavLanguage,
  PublicNavDestinations,
} from "./types";
import { getTrustNavConfig } from "./trust";
import { getCompanyNavConfig } from "./company";

/**
 * Footer configuration. Five curated taxonomy columns shared by Swedish and English public sites. `Footer.tsx` builds legal
 * links and copyright inline, so those never belonged here as config.
 *
 * Curated lists only — not a 1:1 mega-menu dump — so crawl equity stays on
 * hub and commercial URLs instead of every leaf page.
 */
export type FooterSection = { label: string; items: NavItem[] };

export type FooterConfig = {
  product: FooterSection;
  /** Commercial segment / persona pages — high-intent money URLs. */
  solutions: FooterSection;
  trust: FooterSection;
  /** Learning and support hubs; detailed links live on their owning pages. */
  resources: FooterSection;
  company: FooterSection;
};

/**
 * Builds the visitor hubs and company links for the given language.
 *
 * @param t - Translator scoped to the "footer" namespace.
 * @param language - Active site language.
 */
export const getPublicFooterConfig = (
  t: NavTranslate,
  language: PublicNavLanguage,
  destinations: PublicNavDestinations,
): FooterConfig => {
  const isSwedish = language === "sv";
  const ctx = { t, language, isSwedish, destinations };

  // Every key below is a flat top-level entry in footer.json (sv/en) — not
  // "footer.x" or "nav.x". Those prefixes look like namespace paths but are
  // not: `getFooterConfig` was never wired to a live translator before this
  // (only ever called from a test with a no-op `t`), so the mismatch shipped
  // silently. `Footer.tsx` scopes its `t` to the single "footer" namespace,
  // so a prefixed key here would resolve to nothing and always render the
  // (English-only) fallback — a real, user-visible localization bug on the
  // Swedish site once this config actually renders anything.
  return {
    product: {
      label: isSwedish ? "Plattform" : "Platform",
      items: [
        {
          label: isSwedish ? "Plattformsöversikt" : "Platform overview",
          href: isSwedish ? "/plattform" : "/en/platform",
        },
        {
          label: isSwedish ? "Integrationer" : "Integrations",
          href: isSwedish
            ? "/plattform/integrationer"
            : "/en/platform/integrations",
        },
        {
          label: isSwedish ? "Rapporter" : "Reports",
          href: isSwedish
            ? "/produkt/funktioner/rapporter"
            : "/en/product/features/reports",
        },
        {
          label: isSwedish ? "Interaktiv demo" : "Interactive demo",
          href: isSwedish
            ? "/produkt/interaktiv-demo"
            : "/en/product/interactive-demo",
        },
        {
          label: t("pricing", isSwedish ? "Prissättning" : "Pricing"),
          href: isSwedish ? "/prissattning" : "/en/pricing",
        },
      ],
    },
    solutions: {
      label: t("solutions", isSwedish ? "Lösningar" : "Solutions"),
      items: [
        {
          label: t(
            "solutions_overview",
            isSwedish ? "Alla lösningar" : "All solutions",
          ),
          href: isSwedish ? "/losningar" : "/en/solutions",
        },
        {
          label: t(
            "solutions_private",
            isSwedish ? "Privat hemtjänst" : "Private home care",
          ),
          href: isSwedish
            ? "/losningar/privat-hemtjanst"
            : "/en/solutions/private-home-care",
        },
        {
          label: t(
            "solutions_chain",
            isSwedish ? "Hemtjänstkedja" : "Home care chains",
          ),
          href: isSwedish
            ? "/losningar/hemtjanstkedja"
            : "/en/solutions/home-care-chain",
        },
        {
          label: t(
            "solutions_municipalities",
            isSwedish ? "Kommuner" : "Municipalities",
          ),
          href: isSwedish
            ? "/losningar/kommuner"
            : "/en/solutions/municipalities",
        },
        {
          label: t(
            "solutions_operations",
            isSwedish ? "Verksamhetschef" : "Operations managers",
          ),
          href: isSwedish
            ? "/losningar/verksamhetschef"
            : "/en/solutions/operations-manager",
        },
        {
          label: t(
            "solutions_scheduler",
            isSwedish ? "Samordnare" : "Coordinators",
          ),
          href: isSwedish ? "/losningar/samordnare" : "/en/solutions/scheduler",
        },
      ],
    },
    trust: {
      label: isSwedish ? "Trygghet" : "Trust",
      // One link, as in the header: the trust center lists every document.
      items: [getTrustNavConfig(ctx).submenu.overview],
    },
    resources: {
      label: t("resources", isSwedish ? "Resurser" : "Resources"),
      items: [
        {
          label: isSwedish ? "Översikt" : "Overview",
          href: isSwedish ? "/resurser" : "/en/resources",
        },
        {
          label: isSwedish ? "Blogg" : "Blog",
          href: isSwedish ? "/resurser/blogg" : "/en/resources/blog",
        },
        {
          label: isSwedish ? "Hjälp" : "Help",
          href: isSwedish ? "/hjalp" : "/en/help",
        },
        {
          label: isSwedish
            ? "Utvecklare & integrationer"
            : "Developers & integrations",
          href: isSwedish ? "/resurser/utvecklare" : "/en/resources/developers",
        },
        {
          label: isSwedish ? "Vanliga frågor" : "FAQ",
          href: isSwedish ? "/vanliga-fragor" : "/en/faq",
        },
        {
          label: isSwedish ? "Kalkylator" : "Calculator",
          href: isSwedish ? "/kalkylator" : "/en/calculator",
        },
      ],
    },
    company: getCompanyNavConfig(ctx),
  };
};
